import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import * as api from '../utils/api';

export default function ParentDashboard() {
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'search' | 'shortlist' | 'children' | 'enquiries' | 'offers' | 'messages'>('search');

  // Data state
  const [schools, setSchools] = useState<any[]>([]);
  const [shortlistedSchools, setShortlistedSchools] = useState<any[]>([]);
  const [children, setChildren] = useState<any[]>([]);
  const [enquiries, setEnquiries] = useState<any[]>([]);
  const [offers, setOffers] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedSchool, setSelectedSchool] = useState<any>(null);
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [showChildModal, setShowChildModal] = useState(false);
  const [editingChild, setEditingChild] = useState<any>(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    curriculum: '',
    type: '',
    gender: '',
    minFee: '',
    maxFee: '',
    minAge: '',
    maxAge: '',
  });

  // Form state
  const [enquiryForm, setEnquiryForm] = useState({
    schoolId: '',
    childId: '',
    message: '',
  });

  const [childForm, setChildForm] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    currentYear: '',
    preferredBoarding: '',
    specialNeeds: '',
    notes: '',
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'shortlist') loadShortlist();
    if (activeTab === 'enquiries') loadEnquiries();
    if (activeTab === 'offers') loadOffers();
    if (activeTab === 'messages') loadMessages();
  }, [activeTab]);

  async function loadInitialData() {
    try {
      const [schoolsRes, childrenRes] = await Promise.all([
        api.getSchools(),
        api.getChildren(),
      ]);
      setSchools(schoolsRes.schools || []);
      setChildren(childrenRes.children || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      showToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function loadShortlist() {
    try {
      const res = await api.getShortlist();
      setShortlistedSchools(res.schools || []);
    } catch (error) {
      console.error('Failed to load shortlist:', error);
    }
  }

  async function loadEnquiries() {
    try {
      const res = await api.getEnquiries();
      setEnquiries(res.enquiries || []);
    } catch (error) {
      console.error('Failed to load enquiries:', error);
    }
  }

  async function loadOffers() {
    try {
      const res = await api.getOffers();
      setOffers(res.offers || []);
    } catch (error) {
      console.error('Failed to load offers:', error);
    }
  }

  async function loadMessages() {
    try {
      const res = await api.getMessages();
      setMessages(res.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  }

  async function handleShortlist(schoolId: string, isShortlisted: boolean) {
    try {
      setActionLoading(schoolId);
      if (isShortlisted) {
        await api.removeFromShortlist(schoolId);
        showToast('Removed from shortlist', 'success');
      } else {
        await api.addToShortlist(schoolId);
        showToast('Added to shortlist', 'success');
      }
      // Refresh schools list
      const res = await api.getSchools();
      setSchools(res.schools || []);
      if (activeTab === 'shortlist') {
        await loadShortlist();
      }
    } catch (error) {
      showToast('Failed to update shortlist', 'error');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleSubmitEnquiry(e: React.FormEvent) {
    e.preventDefault();
    try {
      setActionLoading('enquiry');
      await api.createEnquiry({
        school_id: enquiryForm.schoolId,
        child_id: enquiryForm.childId,
        message: enquiryForm.message,
      });
      showToast('Enquiry sent successfully', 'success');
      setShowEnquiryModal(false);
      setEnquiryForm({ schoolId: '', childId: '', message: '' });
      await loadEnquiries();
    } catch (error) {
      showToast('Failed to send enquiry', 'error');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleAddChild(e: React.FormEvent) {
    e.preventDefault();
    try {
      setActionLoading('child');
      // Calculate age from date of birth
      const age = childForm.dateOfBirth
        ? Math.floor((Date.now() - new Date(childForm.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
        : 0;

      const childData = {
        firstName: childForm.firstName,
        lastName: childForm.lastName,
        dateOfBirth: childForm.dateOfBirth,
        age: age,
        currentYear: childForm.currentYear || undefined,
        preferredBoarding: childForm.preferredBoarding || undefined,
        requirements: childForm.specialNeeds || undefined,
        notes: childForm.notes || undefined,
      };

      console.log('Submitting child data:', childData);

      await api.createChild(childData);
      showToast('Child added successfully', 'success');
      setShowChildModal(false);
      setChildForm({ firstName: '', lastName: '', dateOfBirth: '', currentYear: '', preferredBoarding: '', specialNeeds: '', notes: '' });
      const res = await api.getChildren();
      setChildren(res.children || []);
    } catch (error) {
      console.error('Failed to add child:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add child';
      showToast(errorMessage, 'error');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleUpdateChild(e: React.FormEvent) {
    e.preventDefault();
    if (!editingChild) return;

    try {
      setActionLoading('child');
      // Calculate age from date of birth
      const age = childForm.dateOfBirth
        ? Math.floor((Date.now() - new Date(childForm.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
        : 0;

      const childData = {
        firstName: childForm.firstName,
        lastName: childForm.lastName,
        dateOfBirth: childForm.dateOfBirth,
        age: age,
        currentYear: childForm.currentYear || undefined,
        preferredBoarding: childForm.preferredBoarding || undefined,
        requirements: childForm.specialNeeds || undefined,
        notes: childForm.notes || undefined,
      };

      console.log('Updating child data:', childData);

      await api.updateChild(editingChild.id, childData);
      showToast('Child updated successfully', 'success');
      setShowChildModal(false);
      setEditingChild(null);
      setChildForm({ firstName: '', lastName: '', dateOfBirth: '', currentYear: '', preferredBoarding: '', specialNeeds: '', notes: '' });
      const res = await api.getChildren();
      setChildren(res.children || []);
    } catch (error) {
      console.error('Failed to update child:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update child';
      showToast(errorMessage, 'error');
    } finally {
      setActionLoading(null);
    }
  }

  function openEditChildModal(child: any) {
    // Parse the name to extract firstName and lastName
    const nameParts = child.name.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    setEditingChild(child);
    setChildForm({
      firstName: firstName,
      lastName: lastName,
      dateOfBirth: child.date_of_birth,
      currentYear: child.current_year_group || '',
      preferredBoarding: child.preferred_boarding || '',
      specialNeeds: child.special_needs || '',
      notes: child.notes || '',
    });
    setShowChildModal(true);
  }

  async function handleAcceptOffer(offerId: string) {
    try {
      setActionLoading(offerId);
      await api.acceptOffer(offerId);
      showToast('Offer accepted!', 'success');
      await loadOffers();
    } catch (error) {
      showToast('Failed to accept offer', 'error');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDeclineOffer(offerId: string) {
    try {
      setActionLoading(offerId);
      await api.declineOffer(offerId);
      showToast('Offer declined', 'success');
      await loadOffers();
    } catch (error) {
      showToast('Failed to decline offer', 'error');
    } finally {
      setActionLoading(null);
    }
  }

  function openEnquiryModal(school: any) {
    setSelectedSchool(school);
    setEnquiryForm({ ...enquiryForm, schoolId: school.id });
    setShowEnquiryModal(true);
  }

  // Filter schools based on search and filters
  const filteredSchools = schools.filter(school => {
    if (searchQuery && !school.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !school.city.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (filters.curriculum && school.curriculum !== filters.curriculum) return false;
    if (filters.type && school.type !== filters.type) return false;
    if (filters.gender && school.gender !== filters.gender) return false;
    if (filters.minFee && school.day_fees < parseInt(filters.minFee)) return false;
    if (filters.maxFee && school.day_fees > parseInt(filters.maxFee)) return false;
    if (filters.minAge && school.age_min > parseInt(filters.minAge)) return false;
    if (filters.maxAge && school.age_max < parseInt(filters.maxAge)) return false;
    return true;
  });

  const tabs = [
    { id: 'search', label: 'Search Schools', icon: '🔍' },
    { id: 'shortlist', label: 'Shortlist', icon: '⭐', count: shortlistedSchools.length },
    { id: 'children', label: 'My Children', icon: '👶', count: children.length },
    { id: 'enquiries', label: 'Enquiries', icon: '📧', count: enquiries.length },
    { id: 'offers', label: 'Offers', icon: '🎓', count: offers.filter(o => o.status === 'pending').length },
    { id: 'messages', label: 'Messages', icon: '💬', count: messages.reduce((sum, m) => sum + (m.unread_count || 0), 0) },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">IndieSchools</h1>
              <p className="text-sm text-gray-600">Welcome, {user?.firstName || user?.email}</p>
            </div>
            <button onClick={logout} className="btn btn-secondary">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4">
          <nav className="flex space-x-8">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
                {tab.count > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-primary-100 text-primary-700 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {/* SEARCH TAB */}
        {activeTab === 'search' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Search Schools</h2>
              <div className="text-sm text-gray-600">
                {filteredSchools.length} {filteredSchools.length === 1 ? 'school' : 'schools'}
              </div>
            </div>

            {/* Search and Filters */}
            <div className="mb-6 space-y-4">
              <input
                type="text"
                placeholder="Search by school name or city..."
                className="input w-full"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

              <div className="grid md:grid-cols-4 lg:grid-cols-7 gap-4">
                <select
                  className="input"
                  value={filters.curriculum}
                  onChange={(e) => setFilters({ ...filters, curriculum: e.target.value })}
                >
                  <option value="">All Curriculums</option>
                  <option value="British">British</option>
                  <option value="IB">IB</option>
                  <option value="Montessori">Montessori</option>
                  <option value="Waldorf">Waldorf</option>
                </select>

                <select
                  className="input"
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                >
                  <option value="">All Types</option>
                  <option value="Independent">Independent</option>
                  <option value="Day">Day</option>
                  <option value="Boarding">Boarding</option>
                  <option value="Prep">Prep</option>
                </select>

                <select
                  className="input"
                  value={filters.gender}
                  onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
                >
                  <option value="">All Genders</option>
                  <option value="Co-educational">Co-educational</option>
                  <option value="Boys">Boys</option>
                  <option value="Girls">Girls</option>
                </select>

                <input
                  type="number"
                  placeholder="Min Fee (£)"
                  className="input"
                  value={filters.minFee}
                  onChange={(e) => setFilters({ ...filters, minFee: e.target.value })}
                />

                <input
                  type="number"
                  placeholder="Max Fee (£)"
                  className="input"
                  value={filters.maxFee}
                  onChange={(e) => setFilters({ ...filters, maxFee: e.target.value })}
                />

                <input
                  type="number"
                  placeholder="Min Age"
                  className="input"
                  value={filters.minAge}
                  onChange={(e) => setFilters({ ...filters, minAge: e.target.value })}
                />

                <input
                  type="number"
                  placeholder="Max Age"
                  className="input"
                  value={filters.maxAge}
                  onChange={(e) => setFilters({ ...filters, maxAge: e.target.value })}
                />
              </div>

              {(searchQuery || Object.values(filters).some(f => f)) && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setFilters({ curriculum: '', type: '', gender: '', minFee: '', maxFee: '', minAge: '', maxAge: '' });
                  }}
                  className="btn btn-secondary"
                >
                  Clear Filters
                </button>
              )}
            </div>

            {/* Schools Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSchools.map(school => (
                <div key={school.id} className="card">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-lg">{school.name}</h3>
                    {school.rating && (
                      <span className="text-sm text-yellow-600">⭐ {school.rating}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-1">{school.city}, {school.postcode}</p>
                  <p className="text-sm text-gray-600 mb-2">
                    {school.curriculum} • {school.type} • Ages {school.age_min}-{school.age_max}
                  </p>
                  <p className="text-sm text-gray-700 mb-3 line-clamp-2">{school.summary}</p>
                  <div className="flex items-center justify-between mb-4">
                    {school.day_fees && (
                      <span className="text-sm font-semibold text-primary-600">
                        £{school.day_fees.toLocaleString()}/year
                      </span>
                    )}
                    <span className={`badge badge-${
                      school.availability === 'available' ? 'success' :
                      school.availability === 'limited' ? 'warning' : 'danger'
                    }`}>
                      {school.availability}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEnquiryModal(school)}
                      className="btn btn-primary flex-1"
                    >
                      Send Enquiry
                    </button>
                    <button
                      onClick={() => handleShortlist(school.id, school.is_shortlisted)}
                      disabled={actionLoading === school.id}
                      className={`btn ${school.is_shortlisted ? 'btn-success' : 'btn-secondary'}`}
                    >
                      {school.is_shortlisted ? '⭐' : '☆'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {filteredSchools.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-600">No schools found matching your criteria</p>
              </div>
            )}
          </div>
        )}

        {/* SHORTLIST TAB */}
        {activeTab === 'shortlist' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">My Shortlist</h2>
            {shortlistedSchools.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {shortlistedSchools.map(school => (
                  <div key={school.id} className="card">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-lg">{school.name}</h3>
                      {school.rating && (
                        <span className="text-sm text-yellow-600">⭐ {school.rating}</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{school.city}</p>
                    <p className="text-sm text-gray-600 mb-4">
                      {school.curriculum} • Ages {school.age_min}-{school.age_max}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEnquiryModal(school)}
                        className="btn btn-primary flex-1"
                      >
                        Send Enquiry
                      </button>
                      <button
                        onClick={() => handleShortlist(school.id, true)}
                        disabled={actionLoading === school.id}
                        className="btn btn-danger"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600 mb-4">No schools in your shortlist yet</p>
                <button
                  onClick={() => setActiveTab('search')}
                  className="btn btn-primary"
                >
                  Browse Schools
                </button>
              </div>
            )}
          </div>
        )}

        {/* CHILDREN TAB */}
        {activeTab === 'children' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">My Children</h2>
              <button
                onClick={() => {
                  setEditingChild(null);
                  setChildForm({ firstName: '', lastName: '', dateOfBirth: '', currentYear: '', preferredBoarding: '', specialNeeds: '', notes: '' });
                  setShowChildModal(true);
                }}
                className="btn btn-primary"
              >
                + Add Child
              </button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {children.map(child => (
                <div key={child.id} className="card">
                  <h3 className="font-semibold text-lg mb-2">
                    {child.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    Date of Birth: {new Date(child.date_of_birth).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-gray-600">
                    Age: {Math.floor((Date.now() - new Date(child.date_of_birth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))} years
                  </p>
                  {child.current_year_group && (
                    <p className="text-sm text-gray-600">Current Year: {child.current_year_group}</p>
                  )}
                  {child.special_needs && (
                    <p className="text-sm text-gray-600 mt-2">Special Needs: {child.special_needs}</p>
                  )}
                  <div className="mt-4">
                    <button
                      onClick={() => openEditChildModal(child)}
                      className="btn btn-secondary w-full"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {children.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-600 mb-4">No children added yet</p>
                <button
                  onClick={() => setShowChildModal(true)}
                  className="btn btn-primary"
                >
                  Add Your First Child
                </button>
              </div>
            )}
          </div>
        )}

        {/* ENQUIRIES TAB */}
        {activeTab === 'enquiries' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">My Enquiries</h2>
            {enquiries.length > 0 ? (
              <div className="space-y-4">
                {enquiries.map(enquiry => (
                  <div key={enquiry.id} className="card">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">{enquiry.school_name}</h3>
                          <span className={`badge badge-${
                            enquiry.status === 'sent' ? 'secondary' :
                            enquiry.status === 'viewed' ? 'primary' :
                            enquiry.status === 'responded' ? 'success' : 'secondary'
                          }`}>
                            {enquiry.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          For: {enquiry.child_name} • {new Date(enquiry.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-700">{enquiry.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600 mb-4">You haven't sent any enquiries yet</p>
                <button
                  onClick={() => setActiveTab('search')}
                  className="btn btn-primary"
                >
                  Browse Schools
                </button>
              </div>
            )}
          </div>
        )}

        {/* OFFERS TAB */}
        {activeTab === 'offers' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Offers Received</h2>
            {offers.length > 0 ? (
              <div className="space-y-4">
                {offers.map(offer => (
                  <div key={offer.id} className="card">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">{offer.school_name}</h3>
                          <span className={`badge badge-${
                            offer.status === 'pending' ? 'warning' :
                            offer.status === 'accepted' ? 'success' :
                            offer.status === 'declined' ? 'danger' : 'secondary'
                          }`}>
                            {offer.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          For: {offer.child_name} • Year {offer.year_group}
                        </p>
                        <p className="text-sm text-gray-700 mb-3">{offer.message}</p>
                        <div className="flex items-center gap-4">
                          <span className="text-lg font-semibold text-primary-600">
                            £{offer.fee?.toLocaleString()}/year
                          </span>
                          {offer.scholarship_amount > 0 && (
                            <span className="text-sm text-green-600">
                              Scholarship: £{offer.scholarship_amount.toLocaleString()}
                            </span>
                          )}
                        </div>
                        {offer.status === 'pending' && (
                          <div className="flex gap-2 mt-4">
                            <button
                              onClick={() => handleAcceptOffer(offer.id)}
                              disabled={actionLoading === offer.id}
                              className="btn btn-success"
                            >
                              Accept Offer
                            </button>
                            <button
                              onClick={() => handleDeclineOffer(offer.id)}
                              disabled={actionLoading === offer.id}
                              className="btn btn-danger"
                            >
                              Decline
                            </button>
                          </div>
                        )}
                        {offer.status === 'accepted' && offer.confirmed_years > 0 && (
                          <p className="text-sm text-green-600 mt-2">
                            ✓ Active placement ({offer.confirmed_years} {offer.confirmed_years === 1 ? 'year' : 'years'})
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600">No offers received yet</p>
              </div>
            )}
          </div>
        )}

        {/* MESSAGES TAB */}
        {activeTab === 'messages' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Messages</h2>
            {messages.length > 0 ? (
              <div className="space-y-4">
                {messages.map(thread => (
                  <div key={thread.thread_id} className="card hover:shadow-md transition-shadow cursor-pointer">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">{thread.school_name || thread.other_party}</h3>
                          {thread.unread_count > 0 && (
                            <span className="badge badge-danger">{thread.unread_count} new</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {new Date(thread.last_message_at).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-700 line-clamp-2">{thread.last_message}</p>
                      </div>
                      <button className="btn btn-primary ml-4">View</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600">No messages yet</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Enquiry Modal */}
      {showEnquiryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <h2 className="text-2xl font-bold mb-4">Send Enquiry to {selectedSchool?.name}</h2>
            <form onSubmit={handleSubmitEnquiry} className="space-y-4">
              <div>
                <label className="label">Select Child</label>
                <select
                  className="input w-full"
                  value={enquiryForm.childId}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, childId: e.target.value })}
                  required
                >
                  <option value="">Select a child</option>
                  {children.map(child => (
                    <option key={child.id} value={child.id}>
                      {child.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Message</label>
                <textarea
                  className="input w-full"
                  rows={6}
                  placeholder="Tell the school about your child and what you're looking for..."
                  value={enquiryForm.message}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, message: e.target.value })}
                  required
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={actionLoading === 'enquiry'}
                  className="btn btn-primary flex-1"
                >
                  {actionLoading === 'enquiry' ? 'Sending...' : 'Send Enquiry'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEnquiryModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Child Modal */}
      {showChildModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <h2 className="text-2xl font-bold mb-4">{editingChild ? 'Edit Child' : 'Add Child'}</h2>
            <form onSubmit={editingChild ? handleUpdateChild : handleAddChild} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="label">First Name</label>
                  <input
                    type="text"
                    className="input w-full"
                    value={childForm.firstName}
                    onChange={(e) => setChildForm({ ...childForm, firstName: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="label">Last Name</label>
                  <input
                    type="text"
                    className="input w-full"
                    value={childForm.lastName}
                    onChange={(e) => setChildForm({ ...childForm, lastName: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Date of Birth</label>
                  <input
                    type="date"
                    className="input w-full"
                    value={childForm.dateOfBirth}
                    onChange={(e) => setChildForm({ ...childForm, dateOfBirth: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="label">Current Year (optional)</label>
                  <input
                    type="text"
                    className="input w-full"
                    placeholder="e.g. Year 5"
                    value={childForm.currentYear}
                    onChange={(e) => setChildForm({ ...childForm, currentYear: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="label">Preferred Boarding (optional)</label>
                <select
                  className="input w-full"
                  value={childForm.preferredBoarding}
                  onChange={(e) => setChildForm({ ...childForm, preferredBoarding: e.target.value })}
                >
                  <option value="">Select preference</option>
                  <option value="Day">Day</option>
                  <option value="Possible Boarder">Possible Boarder</option>
                  <option value="Boarder">Boarder</option>
                </select>
              </div>

              <div>
                <label className="label">Additional Needs (optional)</label>
                <textarea
                  className="input w-full"
                  rows={3}
                  placeholder="Any special educational needs or requirements..."
                  value={childForm.specialNeeds}
                  onChange={(e) => setChildForm({ ...childForm, specialNeeds: e.target.value })}
                />
              </div>

              <div>
                <label className="label">Notes (optional)</label>
                <textarea
                  className="input w-full"
                  rows={3}
                  placeholder="Any additional notes about your child..."
                  value={childForm.notes}
                  onChange={(e) => setChildForm({ ...childForm, notes: e.target.value })}
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={actionLoading === 'child'}
                  className="btn btn-primary flex-1"
                >
                  {actionLoading === 'child'
                    ? (editingChild ? 'Updating...' : 'Adding...')
                    : (editingChild ? 'Update Child' : 'Add Child')
                  }
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowChildModal(false);
                    setEditingChild(null);
                    setChildForm({ firstName: '', lastName: '', dateOfBirth: '', currentYear: '', preferredBoarding: '', specialNeeds: '', notes: '' });
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
